import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText, Check, RotateCcw, Trash2, ExternalLink, X, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { cn } from '@/lib/utils';

type Filtro = 'todos' | 'aberto' | 'resolvido';

interface FeedbackRow {
  id: string;
  pagina: string;
  seletor?: string | null;
  elemento?: string | null;
  comentario: string;
  screenshotUrl?: string | null;
  status: string;
  createdAt: string;
  autor?: { nome: string; email: string } | null;
}

export function AdminFeedbacksList() {
  const [feedbacks, setFeedbacks] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<Filtro>('aberto');
  const [zoom, setZoom] = useState<string | null>(null); // print aberto em tamanho real

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listarFeedbacks(filtro === 'todos' ? undefined : filtro);
      setFeedbacks(res.success && Array.isArray(res.data) ? res.data : []);
    } catch {
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternarStatus = async (f: FeedbackRow) => {
    const status = f.status === 'aberto' ? 'resolvido' : 'aberto';
    const res = await apiService.atualizarFeedback(f.id, { status });
    if (res.success) carregar();
  };

  const remover = async (f: FeedbackRow) => {
    if (!confirm('Remover este feedback?')) return;
    const res = await apiService.removerFeedback(f.id);
    if (res.success) carregar();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">Feedbacks</h1>
          <p className="text-sm text-schmersal-gray-medium">Comentários de correção apontados nas telas do sistema</p>
        </div>
        <div className="flex rounded-lg border border-schmersal-gray bg-white p-0.5 dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darker">
          {(['aberto', 'resolvido', 'todos'] as Filtro[]).map((f) => (
            <button key={f} onClick={() => setFiltro(f)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium capitalize transition',
                filtro === f
                  ? 'bg-schmersal-blue text-white'
                  : 'text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark'
              )}>
              {f === 'aberto' ? 'Abertos' : f === 'resolvido' ? 'Resolvidos' : 'Todos'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-schmersal-gray-medium">
          <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Carregando...
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="rounded-xl border border-schmersal-gray bg-white p-10 text-center dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darker">
          <MessageSquareText className="mx-auto h-10 w-10 text-schmersal-gray-medium" />
          <p className="mt-3 font-medium text-schmersal-gray-dark dark:text-white">
            {filtro === 'aberto' ? 'Nenhum feedback aberto' : 'Nenhum feedback encontrado'}
          </p>
          <p className="mt-1 text-sm text-schmersal-gray-medium">
            Use o botão flutuante no canto da tela para apontar uma correção.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map((f) => (
            <div key={f.id}
              className={cn(
                'rounded-xl border border-schmersal-gray bg-white p-4 dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darker',
                f.status === 'resolvido' && 'opacity-60'
              )}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                {f.screenshotUrl && (
                  <button onClick={() => setZoom(f.screenshotUrl!)} title="Ampliar print"
                    className="shrink-0 overflow-hidden rounded-lg border border-schmersal-gray transition hover:ring-2 hover:ring-schmersal-blue dark:border-schmersal-gray-dark">
                    <img src={f.screenshotUrl} alt="Print da correção" className="h-24 w-36 bg-schmersal-gray-light object-contain dark:bg-schmersal-gray-darkest" />
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      f.status === 'aberto'
                        ? 'bg-schmersal-yellow/20 text-schmersal-yellow-dark'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    )}>
                      {f.status === 'aberto' ? 'Aberto' : 'Resolvido'}
                    </span>
                    <Link to={f.pagina} className="flex items-center gap-1 text-sm font-medium text-schmersal-blue hover:underline dark:text-schmersal-turquoise">
                      {f.pagina} <ExternalLink className="h-3 w-3" />
                    </Link>
                    {f.elemento && (
                      <code className="max-w-full truncate rounded bg-schmersal-gray-light px-1.5 py-0.5 text-xs text-schmersal-gray-dark dark:bg-schmersal-gray-darkest dark:text-schmersal-gray-light" title={f.seletor || undefined}>
                        {f.elemento}
                      </code>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">{f.comentario}</p>
                  <div className="mt-2 text-xs text-schmersal-gray-medium">
                    {f.autor?.nome || 'Anônimo'} · {new Date(f.createdAt).toLocaleString('pt-BR')}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => alternarStatus(f)}
                    title={f.status === 'aberto' ? 'Marcar como resolvido' : 'Reabrir'}
                    className={cn(
                      'rounded-lg p-2 transition',
                      f.status === 'aberto'
                        ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                        : 'text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark'
                    )}>
                    {f.status === 'aberto' ? <Check className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                  </button>
                  <button onClick={() => remover(f)} title="Remover"
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Print em tamanho real */}
      {zoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setZoom(null)}>
          <img src={zoom} alt="Print da correção" className="max-h-full max-w-full rounded-lg bg-white shadow-2xl" />
          <button onClick={() => setZoom(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
